import { useState, type ClipboardEvent, type FormEvent } from 'react'
import { beginSignIn, storeSession, type AuthConfig } from '../lib/auth'
import { Toggletip } from '../components/kit'
import { ErrorReference } from '../components/ui'

// A refusal's code and reference (release 3.1, R3-OI-107): the code the API
// sends and the request id it puts on every answer, so a person who cannot
// sign in has something support can look up.
type Ref = { code: string; reference: string }
function refOf(res: Response, out: { code?: string; error?: string; requestId?: string } | null): Ref {
  return {
    code: out?.code || out?.error || 'HTTP_' + res.status,
    reference: out?.requestId || res.headers.get('X-Request-ID') || '',
  }
}
const NO_ANSWER: Ref = { code: 'NETWORK_ERROR', reference: '' }

// The screen a reader who is not signed in sees instead of the console.
//
// It is a whole screen rather than a banner over the portfolio, because a
// portfolio rendered behind a sign-in prompt is a portfolio that has already
// fired a dozen requests that all failed, and the reader is left deciding
// whether the estate is empty or their session is.
//
// Two ways in. The form is the ordinary one: an email and a password, cleaned
// of what a paste adds (the trailing space, the invisible characters a chat
// client leaves), with the password visible on request, and the temporary
// password of a first sign-in replaced in place. The button below it sends
// the reader to the organisation's own sign-in page, which is the path for
// single sign-on and for anything the form does not do.
//
// Every line under a field or a button is one short line, and the reason
// behind it is an (i). The tips only show words already on this screen: there
// is no session yet, so nothing here may ask the API what the reader can do.

// What a chat client, a PDF or a spreadsheet leaves inside a copied
// credential. None of it was ever typed, so none of it is kept.
const INVISIBLE = /[\u200B\u200C\u200D\u2060\uFEFF\u00A0]/g
function clean(v: string): string {
  return v.replace(INVISIBLE, '').trim()
}

// mfa: a right password on an account with a second factor, waiting for the
// code (release 3.1). mfa-enrol: an administrator the tenant requires one of,
// setting it up before any session exists. recovery: the recovery codes,
// shown once, before the console opens.
type Stage = 'signin' | 'new-password' | 'forgot' | 'reset' | 'mfa' | 'mfa-enrol' | 'recovery'

type Enrolment = { qrPng: string; secret: string; otpauthUri: string; account: string }

// The address to land on after a password sign-in: the one the person
// arrived with, or Home when they arrived with none.
export function afterSignIn(hash: string): string {
  return hash && hash !== '#' && hash !== '#/' ? hash : '#/home'
}

export function SignIn({ config, notice }: { config: AuthConfig; notice?: string }) {
  const [starting, setStarting] = useState(false)
  const [error, setErrorText] = useState('')
  const [errorRef, setErrorRef] = useState<Ref | null>(null)
  // Every failure says what went wrong, and a failure the server answered
  // carries its code and reference; clearing the message clears both.
  function setError(text: string, ref: Ref | null = null) {
    setErrorText(text)
    setErrorRef(text === '' ? null : ref)
  }
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [newAgain, setNewAgain] = useState('')
  const [show, setShow] = useState(false)
  const [stage, setStage] = useState<Stage>('signin')
  const [session, setSession] = useState('')
  const [code, setCode] = useState('')
  const [sentNote, setSentNote] = useState('')
  const [busy, setBusy] = useState(false)
  // The second factor's step: its token, the setup an administrator is taken
  // through, and the recovery codes shown once before the session opens.
  const [mfaToken, setMfaToken] = useState('')
  const [mfaCode, setMfaCode] = useState('')
  const [enrolment, setEnrolment] = useState<Enrolment | null>(null)
  const [recovery, setRecovery] = useState<string[]>([])
  const [pendingSession, setPendingSession] = useState('')

  // A session opened: kept, and the console opened where the person was
  // heading.
  function finish(accessToken: string) {
    storeSession(accessToken, '', 0)
    window.location.hash = afterSignIn(window.location.hash)
    window.location.reload()
  }

  // Back to the start, when the step has ended or the person chooses to.
  function restart(text = '') {
    setMfaToken('')
    setMfaCode('')
    setEnrolment(null)
    setStage('signin')
    setError(text)
  }

  async function mfaPost(path: string, body: Record<string, string>) {
    const res = await fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    })
    const out = (await res.json().catch(() => ({}))) as Record<string, unknown>
    return { res, out }
  }

  // The code from the app, or a recovery code, after a right password.
  async function verifyCode() {
    setBusy(true)
    setError('')
    try {
      const { res, out } = await mfaPost('/v1/auth/mfa/verify', { mfaToken, code: clean(mfaCode) })
      if (res.ok && typeof out.accessToken === 'string') {
        finish(out.accessToken)
        return
      }
      if (out.error === 'MFA_STEP_ENDED' || (out.error === 'MFA_CODE_WRONG' && out.attemptsLeft === 0)) {
        restart(String(out.message ?? 'This sign-in has ended. Start again.'))
        return
      }
      setMfaCode('')
      setError(String(out.message ?? 'That code was not accepted.'), refOf(res, out as Ref & { error?: string }))
    } catch {
      setError('The service could not be reached.', NO_ANSWER)
    } finally {
      setBusy(false)
    }
  }

  // The setup an administrator is taken through before their first session.
  async function startEnrolment(token: string) {
    const { res, out } = await mfaPost('/v1/auth/mfa/enrol', { mfaToken: token })
    if (!res.ok) {
      restart(String(out.message ?? 'The second factor could not be started. Sign in again.'))
      return
    }
    setEnrolment({
      qrPng: String(out.qrPng ?? ''),
      secret: String(out.secret ?? ''),
      otpauthUri: String(out.otpauthUri ?? ''),
      account: String(out.account ?? ''),
    })
    setStage('mfa-enrol')
  }

  async function confirmEnrolment() {
    setBusy(true)
    setError('')
    try {
      const { res, out } = await mfaPost('/v1/auth/mfa/enrol/confirm', { mfaToken, code: clean(mfaCode) })
      if (res.ok && typeof out.accessToken === 'string') {
        setPendingSession(out.accessToken)
        setRecovery(Array.isArray(out.recoveryCodes) ? (out.recoveryCodes as string[]) : [])
        setStage('recovery')
        return
      }
      if (out.error === 'MFA_STEP_ENDED' || (out.error === 'MFA_CODE_WRONG' && out.attemptsLeft === 0)) {
        restart(String(out.message ?? 'This sign-in has ended. Start again.'))
        return
      }
      setMfaCode('')
      setError(String(out.message ?? 'That code was not accepted.'), refOf(res, out as Ref & { error?: string }))
    } catch {
      setError('The service could not be reached.', NO_ANSWER)
    } finally {
      setBusy(false)
    }
  }

  // Asking for a code. The reply is the same whether or not the address is
  // one we hold, so nothing here can be used to find out who has an account.
  async function askForCode() {
    setBusy(true)
    setError('')
    try {
      const res = await fetch('/v1/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: clean(username) }),
      })
      const out = await res.json().catch(() => ({}))
      if (!res.ok) {
        setError(out.message ?? 'That could not be sent.', refOf(res, out))
        return
      }
      setSentNote(out.note ?? '')
      setStage('reset')
    } catch {
      setError('The service could not be reached.', NO_ANSWER)
    } finally {
      setBusy(false)
    }
  }

  // Returning with the code. Here the refusals ARE told apart: somebody
  // holding a code sent to that address has already proved they read its
  // mail, so a wrong code and a refused password are different things to them.
  async function resetWithCode() {
    setBusy(true)
    setError('')
    try {
      const res = await fetch('/v1/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: clean(username),
          code: clean(code),
          newPassword: clean(newPassword),
        }),
      })
      const out = await res.json().catch(() => ({}))
      if (!res.ok) {
        setError(
          (out.issues ?? []).map((i: { detail: string }) => i.detail).join(' ') ||
            out.message ||
            'That code was not accepted.',
          refOf(res, out),
        )
        return
      }
      setPassword(newPassword)
      setNewPassword('')
      setCode('')
      setSentNote('')
      setStage('signin')
      setError('')
    } catch {
      setError('The service could not be reached.', NO_ANSWER)
    } finally {
      setBusy(false)
    }
  }

  const unavailable = config.mode === 'oidc' && !config.ready ? config.reason : ''
  const withPassword = config.mode === 'oidc' && config.ready && config.passwordSignIn === true
  // TPRM's own accounts: single sign-on is offered only when it is in force
  // (inherited from FOCTTA Core, or configured in TPRM), and said through whom.
  const accountsMode = config.mode === 'oidc' && config.ready && config.accounts === true
  const ssoOffered = !accountsMode || (config.mode === 'oidc' && config.ready && config.sso === true)
  const ssoName = config.mode === 'oidc' && config.ready ? config.ssoName || '' : ''
  const ssoUnavailable = config.mode === 'oidc' && config.ready ? config.ssoUnavailable || '' : ''
  const policy = config.mode === 'oidc' && config.ready ? config.passwordPolicy || '' : ''

  async function start() {
    setStarting(true)
    setError('')
    try {
      await beginSignIn(config)
    } catch (err) {
      setStarting(false)
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  // Paste lands cleaned, so what the reader sees in the box is what is sent.
  function onPaste(set: (v: string) => void) {
    return (e: ClipboardEvent<HTMLInputElement>) => {
      const text = e.clipboardData.getData('text')
      if (text !== '') {
        e.preventDefault()
        set(clean(text))
      }
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    // Enter in a code field answers the step it belongs to, never the
    // password form behind it.
    if (stage === 'mfa') return verifyCode()
    if (stage === 'mfa-enrol') return confirmEnrolment()
    if (stage === 'recovery') return finish(pendingSession)
    setError('')
    if (stage === 'new-password' && newPassword !== newAgain) {
      setError('The two new passwords are not the same.')
      return
    }
    setBusy(true)
    try {
      const body =
        stage === 'new-password'
          ? { username: clean(username), newPassword: clean(newPassword), session }
          : { username: clean(username), password: clean(password) }
      const res = await fetch('/v1/auth/password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      })
      const out = (await res.json().catch(() => ({}))) as {
        accessToken?: string
        refreshToken?: string
        expiresIn?: number
        challenge?: string
        session?: string
        note?: string
        message?: string
        error?: string
        mfaToken?: string
        mfaRequired?: boolean
        mfaEnrolmentRequired?: boolean
      }
      // A right password on an account with a second factor, or one the
      // tenant requires to set one up: not yet a session (release 3.1).
      if (res.ok && out.mfaToken && out.mfaRequired) {
        setMfaToken(out.mfaToken)
        setMfaCode('')
        setPassword('')
        setStage('mfa')
        return
      }
      if (res.ok && out.mfaToken && out.mfaEnrolmentRequired) {
        setMfaToken(out.mfaToken)
        setMfaCode('')
        setPassword('')
        await startEnrolment(out.mfaToken)
        return
      }
      if (res.ok && out.challenge === 'NEW_PASSWORD_REQUIRED' && out.session) {
        setSession(out.session)
        setStage('new-password')
        setPassword('')
        return
      }
      if (!res.ok || !out.accessToken) {
        const remedy = (out as { remedy?: string }).remedy
        setError(
          [out.message || 'Sign-in did not work. Check the email and the password and try again.', remedy]
            .filter(Boolean)
            .join(' '),
          refOf(res, out as { code?: string; error?: string; requestId?: string }),
        )
        return
      }
      storeSession(out.accessToken, out.refreshToken || '', out.expiresIn || 0)
      // Where the person was heading, kept. A link from FOCTTA Consent to one
      // vendor (#/vendor/<id>, persona spec 7.3) arrives before anybody has
      // signed in, and sending every password sign-in to #/home dropped it,
      // while the single sign-on path already kept it (lib/auth.ts). An empty
      // address still goes to the first screen the person's job opens.
      window.location.hash = afterSignIn(window.location.hash)
      window.location.reload()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="signin">
      <div className="panel signin-card">
        <img className="signin-mark signin-mark-light" src="/foctta-header-logo.png" alt="FOCTTA" />
        <img className="signin-mark signin-mark-dark" src="/foctta-header-logo-dark.png" alt="FOCTTA" />
        <h1>Third party risk</h1>
        <p className="muted">For the people who have to answer for it.</p>

        {notice != null && notice !== '' && (
          <div className="error-box" role="alert" style={{ marginTop: 16 }}>
            <p>{notice}</p>
          </div>
        )}

        {unavailable !== '' ? (
          <>
            <div className="error-box" role="alert" style={{ marginTop: 16 }}>
              <h3>Sign-in is not configured</h3>
              <p>{unavailable}</p>
            </div>
            <p className="muted" style={{ fontSize: 12 }}>
              This is a deployment setting, not something to retry{' '}
              <Toggletip label="Who can fix this">
                Whoever operates this deployment points the service at your organisation's identity provider; until then
                there is nowhere to sign you in.
              </Toggletip>
            </p>
          </>
        ) : (
          <>
            {withPassword && (
              <form className="signin-form" onSubmit={(e) => void submit(e)}>
                {stage === 'signin' ? (
                  <>
                    <label>
                      Email
                      <input
                        type="email"
                        autoComplete="username"
                        autoFocus
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        onPaste={onPaste(setUsername)}
                        onBlur={() => setUsername((v) => clean(v))}
                        required
                      />
                    </label>
                    <label>
                      Password
                      <span className="signin-password">
                        <input
                          type={show ? 'text' : 'password'}
                          autoComplete="current-password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          onPaste={onPaste(setPassword)}
                          required
                        />
                        <button type="button" className="ghost" onClick={() => setShow((v) => !v)} aria-pressed={show}>
                          {show ? 'Hide' : 'Show'}
                        </button>
                      </span>
                    </label>
                    <button type="submit" className="primary signin-button" data-action="tprm.(shell).a07" disabled={busy}>
                      {busy ? 'Signing in' : 'Sign in'}
                    </button>
                    <p className="muted" style={{ fontSize: 12, margin: '8px 0 0' }}>
                      Paste is fine, it is cleaned before anything is sent{' '}
                      <Toggletip label="What a paste has removed">
                        Spaces at the ends and invisible characters are removed before anything is sent.
                      </Toggletip>
                    </p>
                    {/* The commonest thing that happens to a person on a
                        sign-in screen, and the form had no answer to it. */}
                    <button
                      type="button"
                      className="ghost signin-forgot"
                      onClick={() => {
                        setError('')
                        setSentNote('')
                        setStage('forgot')
                      }}
                    >
                      I have forgotten my password
                    </button>
                  </>
                ) : stage === 'mfa' ? (
                  <>
                    <p className="note" style={{ marginTop: 12 }}>
                      Enter the six digit code from your authenticator app
                    </p>
                    <label>
                      Code
                      <input
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        autoFocus
                        data-testid="mfa-code"
                        value={mfaCode}
                        onChange={(e) => setMfaCode(e.target.value)}
                        onPaste={onPaste(setMfaCode)}
                        required
                      />
                    </label>
                    <p className="muted" style={{ fontSize: 12, margin: '6px 0 0' }}>
                      Lost your phone? Type one of your recovery codes instead{' '}
                      <Toggletip label="About recovery codes">
                        You were given ten when you set this up. Each works once. With none left, ask your IT Admin to
                        reset your second factor.
                      </Toggletip>
                    </p>
                    <button type="button" className="primary signin-button" data-action="tprm.(shell).a08" disabled={busy} onClick={() => void verifyCode()}>
                      {busy ? 'Checking' : 'Continue'}
                    </button>
                    <button type="button" className="ghost signin-forgot" onClick={() => restart()}>
                      Start again
                    </button>
                  </>
                ) : stage === 'mfa-enrol' && enrolment ? (
                  <>
                    <p className="note" style={{ marginTop: 12 }}>
                      Your organisation asks administrators for a second factor. Set one up to continue
                    </p>
                    <ol className="muted" style={{ fontSize: 13, paddingLeft: 18, margin: '8px 0' }}>
                      <li>Open an authenticator app on your phone (Microsoft Authenticator, Google Authenticator or another).</li>
                      <li>Scan this code, or type the key below into the app.</li>
                      <li>Enter the six digit code the app shows.</li>
                    </ol>
                    {enrolment.qrPng !== '' && (
                      <img className="mfa-qr" src={enrolment.qrPng} alt={'QR code for ' + enrolment.account} width={180} height={180} />
                    )}
                    <p className="muted" style={{ fontSize: 12, margin: '6px 0 0' }}>
                      Key <code data-testid="mfa-secret">{enrolment.secret}</code>
                    </p>
                    <label>
                      Code from the app
                      <input
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        autoFocus
                        data-testid="mfa-code"
                        value={mfaCode}
                        onChange={(e) => setMfaCode(e.target.value)}
                        onPaste={onPaste(setMfaCode)}
                        required
                      />
                    </label>
                    <button type="button" className="primary signin-button" data-action="tprm.(shell).a09" disabled={busy} onClick={() => void confirmEnrolment()}>
                      {busy ? 'Checking' : 'Turn it on and sign in'}
                    </button>
                    <button type="button" className="ghost signin-forgot" onClick={() => restart()}>
                      Start again
                    </button>
                  </>
                ) : stage === 'recovery' ? (
                  <>
                    <p className="note" style={{ marginTop: 12 }}>
                      Keep these recovery codes somewhere safe. Each signs you in once if you lose your phone
                    </p>
                    <ul className="mfa-recovery" data-testid="mfa-recovery">
                      {recovery.map((c) => (
                        <li key={c}>
                          <code>{c}</code>
                        </li>
                      ))}
                    </ul>
                    <p className="muted" style={{ fontSize: 12, margin: '6px 0 0' }}>
                      They are not shown again. You can make a new set from your profile.
                    </p>
                    <button type="button" className="primary signin-button" onClick={() => finish(pendingSession)}>
                      I have saved them, continue
                    </button>
                  </>
                ) : stage === 'forgot' ? (
                  <>
                    {/* The address is asked for again here rather than carried
                        over, because somebody who arrives and clicks this
                        first has not typed one yet, and a form that refuses
                        with no field to correct is a dead end. */}
                    <label>
                      Email
                      <input
                        type="email"
                        autoComplete="username"
                        autoFocus
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        onPaste={onPaste(setUsername)}
                        required
                      />
                    </label>
                    <p className="note" style={{ marginTop: 12 }}>
                      We will send a six digit code to your address, it lasts an hour
                    </p>
                    <button type="button" className="primary signin-button" data-action="tprm.(shell).a05" disabled={busy} onClick={() => void askForCode()}>
                      {busy ? 'Sending' : 'Send me a code'}
                    </button>
                    <button type="button" className="ghost signin-forgot" onClick={() => setStage('signin')}>
                      Back to signing in
                    </button>
                  </>
                ) : stage === 'reset' ? (
                  <>
                    {sentNote && <p className="note" style={{ marginTop: 12 }}>{sentNote}</p>}
                    <label>
                      The code from the message
                      <input
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        autoFocus
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        onPaste={onPaste(setCode)}
                        required
                      />
                    </label>
                    {accountsMode && policy !== '' && (
                      <p className="muted" style={{ fontSize: 12, margin: '6px 0 0' }}>
                        {policy}
                      </p>
                    )}
                    <label>
                      New password
                      <span className="signin-password">
                        <input
                          type={show ? 'text' : 'password'}
                          autoComplete="new-password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          onPaste={onPaste(setNewPassword)}
                          required
                        />
                        <button type="button" className="ghost" onClick={() => setShow((v) => !v)} aria-pressed={show}>
                          {show ? 'Hide' : 'Show'}
                        </button>
                      </span>
                    </label>
                    <button type="button" className="primary signin-button" data-action="tprm.(shell).a06" disabled={busy} onClick={() => void resetWithCode()}>
                      {busy ? 'Setting it' : 'Set my password'}
                    </button>
                    <button type="button" className="ghost signin-forgot" onClick={() => setStage('signin')}>
                      Back to signing in
                    </button>
                  </>
                ) : (
                  <>
                    {/* The rule stays in sight: it is what the person is about to type
                        against, and a rule behind an (i) is found after a refusal. */}
                    <p className="note" style={{ marginTop: 12 }}>
                      First sign-in, choose a new password to replace the temporary one
                    </p>
                    <p className="muted" style={{ fontSize: 12, margin: '6px 0 0' }}>
                      At least eight characters, with a capital, a small letter, a number and a symbol
                    </p>
                    <label>
                      New password
                      <span className="signin-password">
                        <input
                          type={show ? 'text' : 'password'}
                          autoComplete="new-password"
                          autoFocus
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          onPaste={onPaste(setNewPassword)}
                          required
                        />
                        <button type="button" className="ghost" onClick={() => setShow((v) => !v)} aria-pressed={show}>
                          {show ? 'Hide' : 'Show'}
                        </button>
                      </span>
                    </label>
                    <label>
                      The same again
                      <input
                        type={show ? 'text' : 'password'}
                        autoComplete="new-password"
                        value={newAgain}
                        onChange={(e) => setNewAgain(e.target.value)}
                        onPaste={onPaste(setNewAgain)}
                        required
                      />
                    </label>
                    <button type="submit" className="primary signin-button" data-action="tprm.(shell).a07" disabled={busy}>
                      {busy ? 'Saving' : 'Set the password and sign in'}
                    </button>
                    <button type="button" className="ghost" onClick={() => { setStage('signin'); setSession(''); setError('') }}>
                      Start again
                    </button>
                  </>
                )}
              </form>
            )}

            {accountsMode && !ssoOffered && ssoUnavailable !== '' && (
              <p className="note" style={{ marginTop: 12 }}>
                {ssoUnavailable}
              </p>
            )}
            {ssoOffered && (
            <>
            <button type="button" className={withPassword ? 'signin-button' : 'primary signin-button'} onClick={start} disabled={starting}>
              {starting ? 'Redirecting' : accountsMode ? 'Sign in with SSO' : withPassword ? 'Sign in with your organisation' : 'Sign in'}
            </button>
            <p className="muted" style={{ fontSize: 12 }}>
              {accountsMode ? (
                <>
                  {ssoName ? 'Through ' + ssoName + ', the same sign-in you use there' : 'Through your organisation\'s sign-in'}{' '}
                  <Toggletip label="Who decides what you can do">
                    Single sign-on proves who you are. What you can do here is set on your TPRM account by your IT Admin.
                  </Toggletip>
                </>
              ) : withPassword ? (
                'For single sign-on, or if the form above does not take you in'
              ) : (
                <>
                  You are sent to your identity provider and returned here{' '}
                  <Toggletip label="Where your password goes">
                    This console holds no password and never sees one.
                  </Toggletip>
                </>
              )}
            </p>
            </>
            )}
          </>
        )}

        {error !== '' && (
          <div className="error-box" role="alert" data-testid="error-box">
            <p>{error}</p>
            {errorRef && <ErrorReference code={errorRef.code} reference={errorRef.reference} />}
          </div>
        )}
      </div>
    </div>
  )
}
